import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { FileUpload } from 'primereact/fileupload';
import {
    ComplaintQuickViewResponse,
    UpdateComplaintRequest,
    updateComplaint
} from '../../services/order/order.service';
import orderServices from '../../services/orders/orderservice';
import { getComplaintStatus } from '../../services/complaint/ComplaintService';
import { DropdownFilterEvent } from 'primereact/dropdown';
import { userOption, userResult } from '../../models/orders/order-list-model';
import './RegisterComplaintDialog.css';

interface ComplaintEditDialogProps {
    visible: boolean;
    complaint: ComplaintQuickViewResponse | null;
    onHide: () => void;
    onSuccess: () => void;
    onSave?: (data: UpdateComplaintRequest) => Promise<any>;
    isCustomerView?: boolean;
    showInternalFields?: boolean;
}

const ComplaintEditDialog: React.FC<ComplaintEditDialogProps> = ({
    visible,
    complaint,
    onHide,
    onSuccess,
    onSave,
    isCustomerView = false,
    showInternalFields = false
}) => {
    const toast = useRef<Toast>(null);
    const fileUploadRef = useRef<FileUpload>(null);
    const [loading, setLoading] = useState(false);
    const [statuses, setStatuses] = useState<any[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [existingAttachments, setExistingAttachments] = useState<any[]>([]);

    // FIX: Always initialize as empty array to prevent .map crash
    const [orderUsers, setOrderUsers] = useState<userOption[]>([]);
    const [orderUser, setOrderUser] = useState<userResult[]>([]);

    // Form state
    const [formData, setFormData] = useState<UpdateComplaintRequest>({
        id: 0,
        complaintTypeId: 0,
        description: '',
        priority: '',
        statusId: 0,
        actionDescription: '',
        correctiveAction: '',
        preventiveAction: '',
        actionDate: '',
        actionBy: 0,
        costPerQtyInRs: 0,
        customerId: 0,
        invoiceId: 0,
        orderDetailId: 0,
        attachments: []
    });

    const complaintTypes = [
        { label: 'Internal', value: 1 },
        { label: 'Product', value: 2 },
        { label: 'Service', value: 3 }
    ];

    const priorityOptions = [
        { label: 'Low', value: 'LOW' },
        { label: 'Medium', value: 'MEDIUM' },
        { label: 'High', value: 'HIGH' }
    ];

    useEffect(() => {
        if (visible) {
            loadStatuses();
        }
    }, [visible]);

    useEffect(() => {
        if (visible && complaint) {

            const newFormData = {
                id: complaint.id,
                complaintTypeId: complaint.complaintTypeId,
                description: complaint.description || '',
                priority: complaint.priority ? complaint.priority.toUpperCase() : '',
                statusId: complaint.statusId,
                actionDescription: complaint.actionDescription || '',
                correctiveAction: complaint.correctiveAction || '',
                preventiveAction: complaint.preventiveAction || '',
                actionDate: complaint.actionDate || '',
                actionBy: complaint.actionBy || 0,
                costPerQtyInRs: complaint.costPerQtyInRs || 0,
                customerId: complaint.productDetail?.customerId || complaint.serviceDetail?.customerId || 0,
                invoiceId: complaint.productDetail?.invoiceId || 0,
                orderDetailId: 0,
                attachments: []
            };

            // Auto-fill Action By with current user if empty
            if (!newFormData.actionBy || newFormData.actionBy === 0) {
                const userStr = localStorage.getItem("user");
                if (userStr) {
                    try {
                        const user = JSON.parse(userStr);
                        const currentUserId = user.userID || user.userId || user.id || user.Id;
                        if (currentUserId) {
                            newFormData.actionBy = currentUserId;
                        }
                    } catch (e) {
                        console.error("Error parsing user from storage", e);
                    }
                }
            }

            setFormData(newFormData);
            setUploadedFiles([]);

            // Load existing attachments
            if (complaint.attachments && Array.isArray(complaint.attachments)) {
                setExistingAttachments(complaint.attachments);
            } else {
                setExistingAttachments([]);
            }

            // Set default action date to today if empty
            if (!newFormData.actionDate) {
                setFormData(prev => ({ ...prev, actionDate: new Date().toISOString() }));
            }
        }
    }, [visible, complaint]);

    // FIX: Guard with Array.isArray before mapping to prevent crash
    useEffect(() => {
        if (!Array.isArray(orderUsers)) return;
        setOrderUser(
            orderUsers.map((user) => ({
                userID: user.userID,
                detail:
                    user.userID !== 0
                        ? user.primaryContactNbr
                            ? `${user.name}, ${user.primaryContactNbr}`
                            : user.name
                        : '',
            }))
        );
    }, [orderUsers]);

    // Load initial users for dropdown
    useEffect(() => {
        if (visible) {
            orderServices.getOnlyAdminUsers('').then((response: any) => {
                // FIX: Safely unwrap array — API may return { data: [...] } or [...] or null
                let rawUsers: any[] = [];
                if (Array.isArray(response)) {
                    rawUsers = response;
                } else if (response && Array.isArray(response.data)) {
                    rawUsers = response.data;
                } else if (response && Array.isArray(response.result)) {
                    rawUsers = response.result;
                } else {
                    rawUsers = [];
                }

                let users = [...rawUsers];

                // 1. Ensure the selected actionBy user (from complaint) is in the list
                if (complaint && complaint.actionBy && complaint.actionBy > 0) {
                    const exists = users.find((u: any) => u.userID === complaint.actionBy);
                    if (!exists && (complaint as any).actionByName) {
                        users = [
                            {
                                userID: complaint.actionBy,
                                name: (complaint as any).actionByName,
                                primaryContactNbr: (complaint as any).actionByPhonenumber || ''
                            },
                            ...users
                        ];
                    }
                }

                // 2. Ensure current logged-in user is in the list
                try {
                    const userStr = localStorage.getItem("user");
                    if (userStr) {
                        const user = JSON.parse(userStr);
                        const currentUserId = user.userID || user.userId || user.id || user.Id;
                        if (currentUserId) {
                            const exists = users.find((u: any) => u.userID === currentUserId);
                            if (!exists) {
                                users = [
                                    {
                                        userID: currentUserId,
                                        name: user.name || user.fullName || user.userName || user.username || "Current User",
                                        primaryContactNbr: user.primaryContactNbr || ""
                                    },
                                    ...users
                                ];
                            }
                        }
                    }
                } catch (e) {
                    console.error("Error adding current user to options", e);
                }

                // FIX: Always set an array (even empty) — never set undefined/object
                setOrderUsers(users.length > 0 ? users : []);
            }).catch((err: any) => {
                console.error("Error loading admin users:", err);
                setOrderUsers([]);
            });
        }
    }, [visible, complaint]);

    const loadStatuses = async () => {
        try {
            const statusList = await getComplaintStatus();

            // FIX: Safely extract array from whatever shape the API returns
            let statusArray: any[] = [];
            if (Array.isArray(statusList)) {
                statusArray = statusList;
            } else if (statusList && Array.isArray((statusList as any).data)) {
                statusArray = (statusList as any).data;
            } else if (statusList && Array.isArray((statusList as any).result)) {
                statusArray = (statusList as any).result;
            }

            const mappedStatuses = statusArray.map((status: any) => ({
                label: String(status.status || status.name || status.statusName || 'Unknown'),
                value: Number(status.complaintStatusId || status.id || status.statusId)
            }));

            setStatuses(mappedStatuses);
        } catch (error) {
            console.error('Error loading statuses:', error);
            setStatuses([]);
        }
    };

    const handleFileUpload = (event: any) => {
        const files = event.files;
        const validFiles: File[] = [];

        files.forEach((file: File) => {
            if (file.size > 2 * 1024 * 1024) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'File Too Large',
                    detail: `${file.name} exceeds 2MB limit`,
                    life: 3000
                });
            } else {
                validFiles.push(file);
            }
        });

        setUploadedFiles(prev => {
            const newFilesList = [...prev, ...validFiles];
            setFormData(currentFormData => ({
                ...currentFormData,
                attachments: newFilesList
            }));
            return newFilesList;
        });
    };

    const removeFile = (index: number) => {
        setUploadedFiles(prev => {
            const newFiles = prev.filter((_, i) => i !== index);
            setFormData(currentFormData => ({
                ...currentFormData,
                attachments: newFiles
            }));
            return newFiles;
        });
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const result = onSave
                ? await onSave(formData)
                : await updateComplaint(formData);
            toast.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Complaint updated successfully',
                life: 3000
            });
            setTimeout(() => {
                onSuccess();
                onHide();
            }, 1000);
        } catch (error: any) {
            console.error('=== UPDATE ERROR ===');
            console.error('Error object:', error);

            let errorMessage = 'Failed to update complaint';

            if (error.response?.data?.errors) {
                const errors = error.response.data.errors;
                const fieldErrors = Object.entries(errors)
                    .map(([field, messages]: [string, any]) => {
                        const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
                        const message = Array.isArray(messages) ? messages[0] : messages;
                        return `${fieldName}: ${message}`;
                    })
                    .join('\n');
                errorMessage = fieldErrors;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.title) {
                errorMessage = error.response.data.title;
            } else if (error.message) {
                errorMessage = error.message;
            }

            toast.current?.show({
                severity: 'error',
                summary: 'Validation Error',
                detail: errorMessage,
                life: 5000,
                sticky: true
            });
        } finally {
            setLoading(false);
        }
    };

    const dialogHeader = (
        <div className="flex align-items-center justify-content-between pb-2 border-bottom-1 surface-border w-full">
            <div className="flex align-items-center gap-2">
                <i className="pi pi-pencil text-green-600 text-lg"></i>
                <span className="font-bold text-lg text-900">Edit Complaint</span>
                <span className="text-500 text-sm ml-2">| CMP-{String(complaint?.id || 0).padStart(3, '0')}</span>
            </div>
            <div className="mr-5">{/* Spacer */}</div>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2 pt-3 border-top-1 surface-border">
            <Button
                label="Cancel"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-text p-button-secondary"
                disabled={loading}
            />
            <Button
                label="Save"
                icon="pi pi-check"
                onClick={handleSubmit}
                className="p-button-success"
                loading={loading}
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} position="top-center" />
            <style>{`
                .action-date-calendar .p-button {
                    background-color: #10b981 !important;
                    border-color: #10b981 !important;
                    color: white !important;
                    border-top-left-radius: 0 !important;
                    border-bottom-left-radius: 0 !important;
                }
                .action-date-calendar .p-button:hover {
                    background-color: #059669 !important;
                    border-color: #059669 !important;
                }
                .action-date-calendar .p-inputtext {
                    border-right: none !important;
                }
                .action-date-calendar .p-button {
                    border-left: none !important;
                }
                .complaint-edit-dropdown-panel.p-dropdown-panel {
                    border-radius: 12px !important;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
                    border: 1px solid #f0f0f0 !important;
                    padding: 0.5rem !important;
                }
                .complaint-edit-dropdown-panel.p-dropdown-panel .p-dropdown-items {
                    padding: 0 !important;
                }
                .complaint-edit-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
                    border-radius: 8px !important;
                    margin: 2px 0 !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.875rem !important;
                    transition: all 0.2s ease !important;
                }
                .complaint-edit-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
                    background: #4CAF50 !important;
                    color: #ffffff !important;
                }
                .complaint-edit-dropdown-panel.p-dropdown-panel .p-dropdown-items .p-dropdown-item.p-highlight {
                    background: #4CAF50 !important;
                    color: #ffffff !important;
                    font-weight: 600 !important;
                }
            `}</style>
            <Dialog
                visible={visible}
                onHide={onHide}
                header={dialogHeader}
                footer={dialogFooter}
                style={{ width: '700px' }}
                breakpoints={{ '960px': '90vw', '641px': '95vw' }}
                modal
                draggable={false}
                contentClassName="p-0 complaint-dialog-content-fix"
                className="complaint-dialog"
            >
                <div className="complaint-custom-scroll">
                    <div className="p-3">
                        {/* Basic Info */}
                        <div className="grid p-fluid text-sm mb-3">
                            <div className="col-12 md:col-4">
                                <label htmlFor="complaintType" className="font-bold text-xs mb-1 block">Type *</label>
                                <Dropdown
                                    id="complaintType"
                                    value={formData.complaintTypeId}
                                    options={complaintTypes}
                                    onChange={(e) => setFormData({ ...formData, complaintTypeId: e.value })}
                                    placeholder="Select"
                                    className="w-full"
                                    disabled
                                    panelClassName="complaint-edit-dropdown-panel"
                                />
                            </div>

                            {showInternalFields && (
                                <>
                                    <div className="col-12 md:col-4">
                                        <label htmlFor="status" className="font-bold text-xs mb-1 block">Status *</label>
                                        <Dropdown
                                            id="status"
                                            value={formData.statusId}
                                            options={statuses}
                                            onChange={(e) => setFormData({ ...formData, statusId: e.value })}
                                            placeholder="Select Status"
                                            className="w-full"
                                            panelClassName="complaint-edit-dropdown-panel"
                                        />
                                    </div>
                                    <div className="col-12 md:col-4">
                                        <label htmlFor="priority" className="font-bold text-xs mb-1 block">Priority *</label>
                                        <Dropdown
                                            id="priority"
                                            value={formData.priority}
                                            options={priorityOptions}
                                            onChange={(e) => setFormData({ ...formData, priority: e.value })}
                                            placeholder="Select Priority"
                                            className="w-full"
                                            panelClassName="complaint-edit-dropdown-panel"
                                        />
                                    </div>
                                </>
                            )}

                            <div className="col-12">
                                <label htmlFor="description" className="font-bold text-xs mb-1 block">Description *</label>
                                <InputTextarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={2}
                                    placeholder="Description..."
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <Divider className="my-2" />

                        {/* Product Details */}
                        {formData.complaintTypeId === 2 && complaint?.productDetail && (
                            <div className="dialog-section">
                                <div className="dialog-section-title">PRODUCT INFO</div>
                                <div className="grid m-0 text-sm">
                                    <div className="col-4 p-1"><span className="text-500">Product:</span> <span className="font-medium text-900">{complaint.productDetail.productName}</span></div>
                                    <div className="col-4 p-1"><span className="text-500">Inv #:</span> <span className="font-medium text-900">{complaint.productDetail.invoiceNo}</span></div>
                                    <div className="col-4 p-1"><span className="text-500">Customer:</span> <span className="font-medium text-900">{complaint.productDetail.customerName}</span></div>
                                </div>
                            </div>
                        )}

                        {/* Action Details */}
                        {showInternalFields && (
                            <>
                                <div className="grid p-fluid text-sm mb-3">
                                    <div className="col-12 md:col-6">
                                        <label htmlFor="actionDescription" className="font-bold text-xs mb-1 block">Action Description</label>
                                        <InputTextarea
                                            id="actionDescription"
                                            value={formData.actionDescription}
                                            onChange={(e) => setFormData({ ...formData, actionDescription: e.target.value })}
                                            rows={2}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <label htmlFor="correctiveAction" className="font-bold text-xs mb-1 block">Corrective Action</label>
                                        <InputTextarea
                                            id="correctiveAction"
                                            value={formData.correctiveAction}
                                            onChange={(e) => setFormData({ ...formData, correctiveAction: e.target.value })}
                                            rows={2}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <label htmlFor="preventiveAction" className="font-bold text-xs mb-1 block">Preventive Action</label>
                                        <InputTextarea
                                            id="preventiveAction"
                                            value={formData.preventiveAction}
                                            onChange={(e) => setFormData({ ...formData, preventiveAction: e.target.value })}
                                            rows={2}
                                            className="w-full"
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <label htmlFor="actionDate" className="font-bold text-xs mb-1 block">Action Date</label>
                                        <Calendar
                                            id="actionDate"
                                            value={formData.actionDate ? new Date(formData.actionDate) : null}
                                            onChange={(e) => {
                                                if (e.value) {
                                                    const selectedDate = e.value as Date;
                                                    const now = new Date();
                                                    selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
                                                    setFormData({ ...formData, actionDate: selectedDate.toISOString() });
                                                } else {
                                                    setFormData({ ...formData, actionDate: '' });
                                                }
                                            }}
                                            dateFormat="dd/mm/yy"
                                            minDate={complaint?.createdDate ? new Date(complaint.createdDate) : undefined}
                                            maxDate={new Date()}
                                            showIcon
                                            icon="pi pi-calendar"
                                            className="w-full compact-calendar action-date-calendar"
                                            panelClassName="complaint-search-datepicker"
                                            style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                                        />
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <label htmlFor="actionBy" className="font-bold text-xs mb-1 block">Action By</label>
                                        <Dropdown
                                            id="actionBy"
                                            value={formData.actionBy}
                                            // FIX: Guard with Array.isArray — never pass non-array to options
                                            options={Array.isArray(orderUser) ? orderUser : []}
                                            optionLabel="detail"
                                            optionValue="userID"
                                            onChange={(e) => setFormData({ ...formData, actionBy: e.value })}
                                            onFilter={(e: DropdownFilterEvent) => {
                                                if (e.filter && e.filter.length > 2) {
                                                    orderServices.getOnlyAdminUsers(e.filter).then((response: any) => {
                                                        // FIX: Safely unwrap array here too
                                                        const data = Array.isArray(response)
                                                            ? response
                                                            : Array.isArray(response?.data)
                                                                ? response.data
                                                                : [];
                                                        if (data.length > 0) {
                                                            setOrderUsers(data);
                                                        }
                                                    }).catch(() => {});
                                                }
                                            }}
                                            filter
                                            placeholder="Select User"
                                            className="w-full"
                                            showClear
                                            panelClassName="complaint-edit-dropdown-panel"
                                        />
                                    </div>
                                    {formData.complaintTypeId !== 3 && (
                                        <div className="col-12 md:col-6">
                                            <label htmlFor="costPerQty" className="font-bold text-xs mb-1 block">Cost Per Qty (₹)</label>
                                            <InputNumber
                                                id="costPerQty"
                                                value={formData.costPerQtyInRs}
                                                onValueChange={(e) => setFormData({ ...formData, costPerQtyInRs: e.value || 0 })}
                                                mode="currency"
                                                currency="INR"
                                                locale="en-IN"
                                                className="w-full"
                                                style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <Divider className="my-2" />

                        {/* Attachments */}
                        <div className="mb-2">
                            <div className="flex align-items-center justify-content-between mb-2">
                                <label className="font-bold text-sm text-900">Attachments</label>
                                <FileUpload
                                    ref={fileUploadRef}
                                    name="attachments"
                                    multiple
                                    accept="image/*,.pdf"
                                    maxFileSize={2000000}
                                    mode="basic"
                                    customUpload
                                    auto={false}
                                    chooseLabel="Add Files"
                                    className="p-button-sm p-button-success p-button-outlined"
                                    onSelect={(e) => {
                                        handleFileUpload(e);
                                        setTimeout(() => fileUploadRef.current?.clear(), 100);
                                    }}
                                />
                            </div>

                            {/* Existing Attachments */}
                            {existingAttachments.length > 0 && (
                                <ul className="list-none p-0 m-0 mb-2">
                                    {existingAttachments.map((attachment: any, index: number) => (
                                        <li
                                            key={index}
                                            className="flex align-items-center justify-content-between p-2 hover:surface-100 border-round mb-1 border-1 surface-border cursor-pointer"
                                            onClick={() => window.open(attachment.fileUrl, '_blank')}
                                        >
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-file text-700"></i>
                                                <span className="text-sm text-900">{attachment.fileName}</span>
                                            </div>
                                            <i className="pi pi-download text-green-600"></i>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            {/* New Uploads */}
                            {uploadedFiles.length > 0 && (
                                <ul className="list-none p-0 m-0">
                                    {uploadedFiles.map((file, index) => (
                                        <li key={index} className="flex align-items-center justify-content-between p-2 bg-green-50 border-round mb-1">
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-plus text-green-600"></i>
                                                <span className="text-sm font-medium">{file.name}</span>
                                            </div>
                                            <i className="pi pi-times text-red-500 cursor-pointer" onClick={() => removeFile(index)}></i>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default ComplaintEditDialog;